import { getAccount, getAssociatedTokenAddress } from '@solana/spl-token';
import { LAMPORTS_PER_SOL, PublicKey, type Connection } from '@solana/web3.js';
import { CWY_DECIMALS, CWY_MINT_ADDRESS, CWY_IS_MAINNET } from '../config/cwyConfig';

export type WalletCwyBalanceInfo = {
  balance: number;
  ataExists: boolean;
  tokenAccount: PublicKey | null;
  solBalance: number;
};

const MIN_SOL_FOR_DEPOSIT = 0.001;

export async function getWalletCwyBalance(
  connection: Connection,
  owner: PublicKey
): Promise<WalletCwyBalanceInfo> {
  const solLamports = await connection.getBalance(owner);
  const solBalance = solLamports / LAMPORTS_PER_SOL;

  if (!CWY_MINT_ADDRESS) {
    return { balance: 0, ataExists: false, tokenAccount: null, solBalance };
  }

  const mint = new PublicKey(CWY_MINT_ADDRESS);
  const ata = await getAssociatedTokenAddress(mint, owner);

  try {
    const account = await getAccount(connection, ata, 'confirmed');
    // Round to 6 d.p. to eliminate floating-point noise from large integer division
    const balance = Math.round((Number(account.amount) / 10 ** CWY_DECIMALS) * 1e6) / 1e6;
    if (balance > 0) {
      return { balance, ataExists: true, tokenAccount: ata, solBalance };
    }
  } catch {
    // fall through — try parsed token accounts
  }

  const parsed = await connection.getParsedTokenAccountsByOwner(owner, { mint });
  for (const entry of parsed.value) {
    const rawAmount = entry.account.data.parsed?.info?.tokenAmount?.amount;
    if (!rawAmount) continue;
    const balance = Math.round((Number(rawAmount) / 10 ** CWY_DECIMALS) * 1e6) / 1e6;
    if (balance > 0) {
      return {
        balance,
        ataExists: true,
        tokenAccount: entry.pubkey,
        solBalance,
      };
    }
  }

  try {
    await getAccount(connection, ata);
    return { balance: 0, ataExists: true, tokenAccount: ata, solBalance };
  } catch {
    return { balance: 0, ataExists: false, tokenAccount: null, solBalance };
  }
}

export function formatMissingCwyBalanceError(linkedAddress?: string): string {
  const suffix = linkedAddress
    ? ` Linked wallet: ${linkedAddress.slice(0, 8)}…${linkedAddress.slice(-6)}. Make sure this matches the wallet that holds CWY in Nightly.`
    : '';
  return `This linked wallet has no CWY tokens yet. Request CWY to it first, or set the wallet that holds CWY as your primary.${suffix}`;
}

export function formatInsufficientCwyBalanceError(balance: number, amount: number): string {
  return `Insufficient CWY balance. You have ${balance} CWY but tried to deposit ${amount} CWY.`;
}

export function formatInsufficientSolError(solBalance: number): string {
  if (CWY_IS_MAINNET) {
    return `You need a small amount of SOL in this wallet to pay network fees (you have ${solBalance} SOL).`;
  }
  return `You need a small amount of SOL for transaction fees (you have ${solBalance} SOL). On devnet, get free SOL from https://faucet.solana.com — about 0.01 SOL is enough.`;
}

export function assertSufficientSolForDeposit(solBalance: number): void {
  if (solBalance < MIN_SOL_FOR_DEPOSIT) {
    throw new Error(formatInsufficientSolError(solBalance));
  }
}
